import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import {
  apiAnalyticsUserFunnelVenue,
  apiAnalyticsUserSaveVenue,
  apiCallSentimentTrackingAi,
} from "../../../Core/Apicall";
import { useLocation } from "react-router-dom";
import { CircularProgress, Skeleton, Typography } from "@mui/material";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

type FunnelFilter = "today" | "yesterday" | "last7days" | "lastMonth";

const FUNNEL_FILTERS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7days", label: "Last 7 Days" },
  { key: "lastMonth", label: "Last 30 Days" },
];

const SaveBreakDown = ({ venueId }: { venueId: string }) => {
  const [data, setData] = useState<any[]>([]);
  const [funnelData, setFunnelData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelFilter, setFunnelFilter] = useState<FunnelFilter>("today");
  const [sentimentFilter, setSentimentFilter] = useState<FunnelFilter>("today");
  const [chartData, setChartData] = useState<any>(null);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    fetchGetDashBoardGraph();
  }, [sentimentFilter]);

  const fetchGetDashBoardGraph = async () => {
    try {
      setLoading(true);
      const res = await apiCallSentimentTrackingAi({
        venueId,
        filter: sentimentFilter,
      });
      setChartData(res.data.chartData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await apiAnalyticsUserSaveVenue({ venueId });
      setData(res.data);
    } finally {
    }
  };

  const fetchFunnelAnalytics = async (filter: FunnelFilter) => {
    setFunnelLoading(true);
    try {
      const res = await apiAnalyticsUserFunnelVenue({ venueId, filter });
      const d = res.data;
      setFunnelData([
        { label: "Viewing", value: d.viewing, color: "#2563EB" },
        { label: "Saving", value: d.saving, color: "#10B981" },
        { label: "Attending", value: d.attending, color: "#8B5CF6" },
        { label: "Re-visiting", value: d.revisiting, color: "#EC4899" },
      ]);
    } finally {
      setFunnelLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchFunnelAnalytics(funnelFilter);
  }, [funnelFilter]);

  const handleRefresh = async () => {
    await Promise.all([
      fetchAnalytics(),
      fetchFunnelAnalytics(funnelFilter),
      fetchGetDashBoardGraph(),
    ]);
  };

  const sentimentTotals = useMemo(() => {
    if (!chartData?.datasets)
      return { positive: 0, neutral: 0, negative: 0, total: 0 };

    const getTotal = (label: string) => {
      const ds = chartData.datasets.find(
        (d: any) => d.label.toLowerCase() === label
      );
      return ds ? ds.data.reduce((a: number, b: number) => a + b, 0) : 0;
    };

    const positive = getTotal("positive");
    const neutral = getTotal("neutral");
    const negative = getTotal("negative");

    return {
      positive,
      neutral,
      negative,
      total: positive + neutral + negative,
    };
  }, [chartData]);

  const totalSaves = data.reduce((a, b) => a + b.value, 0);
  const viewingUsers =
    funnelData.find((f) => f.label === "Viewing")?.value || 0;

  const saveDonutData = {
    datasets: [
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map((d) => d.color),
        cutout: "78%",
        borderWidth: 0,
      },
    ],
  };

  const funnelDonutData = {
    datasets: [
      {
        data: funnelData.map((f) => f.value),
        backgroundColor: funnelData.map((f) => f.color),
        cutout: "78%",
        borderWidth: 0,
      },
    ],
  };

  /* ===== STYLES ===== */
  const s: any = {
    page: {},
    card: { background: "#fff", borderRadius: 20, padding: 24 },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 24,
      flexWrap: "wrap",
      gap: 12,
    },
    title: { fontSize: 24, fontWeight: 700 },
    refreshBtn: {
      background: "#2563EB",
      color: "#fff",
      border: "none",
      padding: "10px 20px",
      borderRadius: 12,
      cursor: "pointer",
    },
    topGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: 24,
    },
    innerCard: { border: "1px solid #E5E7EB", borderRadius: 20, padding: 20 },
    content: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "220px 1px 1fr",
      gap: 20,
      alignItems: "center",
    },
    centerText: {
      position: "absolute",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
    total: { fontSize: 36, fontWeight: 700 },
    sub: { color: "#6B7280" },
    divider: { width: 1, background: "#E5E7EB" },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 12,
      flexWrap: "wrap",
    },
    rowLabel: { minWidth: 140 },
    progressWrap: {
      flex: 1,
      height: 8,
      background: "#E5E7EB",
      borderRadius: 10,
    },
    progressFill: { height: "100%", borderRadius: 10 },
    funnelHeader: {
      display: "flex",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: 16,
    },
    filterTabs: { display: "flex", gap: 8, flexWrap: "wrap" },
    filterBtn: {
      border: "1px solid #D1D5DB",
      padding: "6px 14px",
      borderRadius: 999,
      background: "#fff",
      cursor: "pointer",
    },
    filterBtnActive: { background: "#2563EB", color: "#fff" },
    funnelBody: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "200px 1fr",
      gap: 20,
      position: "relative",
      alignItems: "center",
      textAlign: isMobile ? "center" : "left",
    },
    funnelDonutWrap: {
      width: 180,
      height: 180,
      position: "relative",
      margin: isMobile ? "0 auto" : "0",
    },
    funnelAmount: { fontSize: 32, fontWeight: 700 },
    legendRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 10,
      justifyContent: isMobile ? "center" : "flex-start",
    },
    legendDot: { width: 10, height: 10, borderRadius: "50%" },
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <h2 style={s.title}></h2>
          <button style={s.refreshBtn} onClick={handleRefresh}>
            {loading || funnelLoading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div style={s.topGrid}>
          {/* SAVE */}
          <div style={s.innerCard}>
            <div style={s.funnelHeader}>
              <h5>Save Reasons Breakdown</h5>
            </div>
            <div style={s.content}>
              <div style={s.funnelDonutWrap}>
                <Doughnut data={saveDonutData} />
                <div style={s.centerText}>
                  <div style={s.total}>{totalSaves}</div>
                  <div style={s.sub}>Total Saves</div>
                </div>
              </div>
              {!isMobile && <div style={s.divider} />}
              <div>
                {data.map((d) => {
                  const percent = totalSaves
                    ? Math.round((d.value / totalSaves) * 100)
                    : 0;
                  return (
                    <div key={d.label} style={s.row}>
                      <span style={s.rowLabel}>{d.label}</span>
                      <div style={s.progressWrap}>
                        <div
                          style={{
                            ...s.progressFill,
                            width: `${percent}%`,
                            background: d.color,
                          }}
                        />
                      </div>
                      <b>{d.value}</b>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* FUNNEL */}
          <div style={s.innerCard}>
            <div style={s.funnelHeader}>
              <h5>User Funnel Tracking</h5>
              <div style={s.filterTabs}>
                {FUNNEL_FILTERS.map((f: any) => (
                  <button
                    key={f.key}
                    onClick={() => setFunnelFilter(f.key)}
                    style={{
                      ...s.filterBtn,
                      ...(funnelFilter === f.key ? s.filterBtnActive : {}),
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={s.funnelBody}>
              {funnelLoading && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    backdropFilter: "blur(2px)",
                    background: "rgba(255,255,255,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress style={{ color: "#000" }} size={30} />
                </div>
              )}
              <div style={s.funnelDonutWrap}>
                <Doughnut data={funnelDonutData} />
                <div style={s.centerText}>
                  <div style={s.funnelAmount}>{viewingUsers}</div>
                  <div style={s.sub}>Viewing Users</div>
                </div>
              </div>
              <div>
                {funnelData.map((f) => (
                  <div key={f.label} style={s.legendRow}>
                    <span style={{ ...s.legendDot, background: f.color }} />
                    <span>{f.label}</span>
                    <b>{f.value}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <div style={s.innerCard}>
            <div style={s.funnelHeader}>
              <h5>Sentiment Analytics</h5>
              <div style={s.filterTabs}>
                {FUNNEL_FILTERS.map((f: any) => (
                  <button
                    key={f.key}
                    onClick={() => setSentimentFilter(f.key)}
                    style={{
                      ...s.filterBtn,
                      ...(sentimentFilter === f.key ? s.filterBtnActive : {}),
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 24,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <b style={{ color: "#16A34A" }}>Positive:</b>{" "}
                {sentimentTotals.positive}
              </div>
              <div>
                <b style={{ color: "#9CA3AF" }}>Neutral:</b>{" "}
                {sentimentTotals.neutral}
              </div>
              <div>
                <b style={{ color: "#EF4444" }}>Negative:</b>{" "}
                {sentimentTotals.negative}
              </div>
              <div>
                <b>Total:</b> {sentimentTotals.total}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 20,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  flex: 2,
                  minHeight: 340,
                  position: "relative", // ⭐ IMPORTANT
                }}
              >
                {/* OVERLAY LOADER */}
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 10,
                      backdropFilter: "blur(2px)",
                      background: "rgba(255,255,255,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress style={{ color: "#000" }} size={30} />
                  </div>
                )}

                {/* CHART */}
                {chartData && (
                  <Line
                    data={chartData}
                    options={{
                      maintainAspectRatio: false,
                      plugins: { legend: { position: "top" } },
                      scales: { y: { beginAtZero: true } },
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveBreakDown;
